import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react'
import Script from 'next/script'
import { useTranslation } from 'next-i18next'

interface SurfboardPhone {
  countryCode: string
  number: string
}

interface SurfboardBillingAddress {
  addressId?: string
  careOf?: string
  addressLine1: string
  addressLine2?: string
  addressLine3?: string
  city: string
  countryCode: string
  postalCode: string
}

interface SurfboardTax {
  type: 'VAT'
  percentage: number
  amount: number
}

interface SurfboardItemPrice {
  value: string
  currency: string
  campaign: string
  regular: string
  tax: SurfboardTax[]
}

interface SurfboardOrderLine {
  externalItemId: string
  gtin: string
  brand: string
  itemPrice: SurfboardItemPrice
  itemId: string
  quantity: number
  unit: string
  name: string
  description?: string
  imageUrl?: string
}

interface SurfboardOrder {
  orderId: string
  terminalId: string
  billingAddress: SurfboardBillingAddress
  orderStatus: string
  orderDate: string
  memberId: string
  totalOrderPriceValue: string
  totalOrderPriceCurrency: string
  memberEmail: string
  memberPhone: SurfboardPhone
  referenceId: string
  orderLines: SurfboardOrderLine[]
  tax: SurfboardTax[]
}

interface SurfboardCustomerInfo {
  phone: SurfboardPhone
  name: string
  email: string
  billingAddress: SurfboardBillingAddress
}

type SurfboardPaymentStatus =
  | 'PENDING'
  | 'PAYMENT_INITIATED'
  | 'PAYMENT_CANCELLED'
  | 'PAYMENT_COMPLETED'
  | 'PAYMENT_FAILED'
  | 'PAYMENT_PROCESSING'
  | 'PAYMENT_PROCESSED'

export type { SurfboardPaymentStatus }

interface SurfboardPaymentStatusCallbackData {
  paymentStatus: SurfboardPaymentStatus
  failureReason?: string
}

type SurfboardPaymentMethod =
  | 'CARD'
  | 'APPLE_PAY'
  | 'GOOGLE_PAY'
  | 'KLARNA'
  | 'SSWISH'
  | 'NSWISH'

export type { SurfboardPaymentMethod }

interface SurfboardPaymentMethods {
  supportedPaymentMethods: SurfboardPaymentMethod[]
  lockToPaymentMethods: SurfboardPaymentMethod[] | null
}

interface SurfboardMerchant {
  name: string
  organizationNumber: string
}

interface SurfboardBranding {
  backgroundColor: string
  brandColor: string
  accentColor: string
  rectShape: 'EDGY' | 'ROUNDED' | 'PILL'
  fontType: 'serif' | 'sans-serif' | 'monospace'
  logoUrl: string
  iconUrl: string
}

interface SurfboardStore {
  name: string
  phoneNumber: SurfboardPhone
  email: string
  address: SurfboardBillingAddress
  privacyPolicyUrl: string
  termAndConditionUrl: string
}

interface SurfboardCustomer {
  name?: string
  email?: string
  phone?: SurfboardPhone
  orderBillingAddress?: SurfboardBillingAddress
  customerId?: string
  savedAddresses?: SurfboardBillingAddress[]
}

type GetPaymentStatus = {
  paymentStatus: SurfboardPaymentStatus
}

export interface PaymentAttempt {
  paymentId: string
  getPaymentStatus(): GetPaymentStatus
  cancelPayment(): void
  getSwishAppRedirectUrl(callback?: string): string
  getSwishQRData: string | null
}

type SurfboardErrorCallback = (
  callback: (code: string, message: string) => void
) => void
type SurfboardPaymentStatusCallback = (
  callback: (data: SurfboardPaymentStatusCallbackData) => void
) => void
type SurfboardMountCallback = (config: Record<string, string>) => void
type SurfboardAddCustomerInformation = (
  customerInfo: SurfboardCustomerInfo
) => Promise<boolean>

// Types
interface InternalSurfboardContextType {
  setErrorCallback: SurfboardErrorCallback
  setPaymentStatusCallback: SurfboardPaymentStatusCallback
  mount: SurfboardMountCallback
  isReady: boolean
  paymentMethods: SurfboardPaymentMethods
  merchant: SurfboardMerchant
  branding: SurfboardBranding
  store: SurfboardStore
  customer: SurfboardCustomer
  order: {
    order: SurfboardOrder
    addCustomerInformation: SurfboardAddCustomerInformation
    initiatePayments: (
      paymentMethod: SurfboardPaymentMethod
    ) => Promise<PaymentAttempt>
  }
  addCustomerInformationOnce: SurfboardAddCustomerInformation
  supportedPaymentMethods: SurfboardPaymentMethod[]
  initiatePayments: (method: SurfboardPaymentMethod) => Promise<PaymentAttempt>
  usedPaymentMethod: SurfboardPaymentMethod | null
}

interface SurfboardContextType {
  supportedPaymentMethods: SurfboardPaymentMethod[]
  initiatePayments: (method: SurfboardPaymentMethod) => Promise<PaymentAttempt>
  setErrorCallback: SurfboardErrorCallback
  setPaymentStatusCallback: SurfboardPaymentStatusCallback
  addCustomerInformationOnce: SurfboardAddCustomerInformation
  isReady: boolean
  usedPaymentMethod: SurfboardPaymentMethod | null
}

interface SurfboardProviderProps {
  publicKey: string
  orderId: string
  nonce: string
  children: React.ReactNode
  options?: {
    locale?: string
  }
}

interface PaymentElementProps {
  type: SurfboardPaymentMethod
}

// Create context
const SurfboardContext = createContext<InternalSurfboardContextType | null>(
  null
)

// Custom hooks
export const useSurfboard = (): SurfboardContextType => {
  const context = useContext(SurfboardContext)
  if (!context) {
    throw new Error('useSurfboard must be used within a SurfboardProvider')
  }
  return {
    supportedPaymentMethods: context.supportedPaymentMethods,
    initiatePayments: context.initiatePayments,
    setErrorCallback: context.setErrorCallback,
    setPaymentStatusCallback: context.setPaymentStatusCallback,
    addCustomerInformationOnce: context.addCustomerInformationOnce,
    isReady: context.isReady,
    usedPaymentMethod: context.usedPaymentMethod,
  }
}

// Custom hooks
const useInternalSurfboard = (): InternalSurfboardContextType => {
  const context = useContext(SurfboardContext)
  if (!context) {
    throw new Error('useSurfboard must be used within a SurfboardProvider')
  }
  return context
}

const addLog = (
  message: string,
  level: 'info' | 'error' | 'success' = 'info'
) => {
  const timestamp = new Date().toISOString()
  const formattedMessage = `[${level.toUpperCase()}] ${timestamp}: ${message}`
  console.log(formattedMessage)
  if (level === 'error') {
    console.error(formattedMessage)
  }
}

export const SurfboardProvider: React.FC<SurfboardProviderProps> = ({
  publicKey,
  orderId,
  nonce,
  children,
  options = {},
}) => {
  const [surfboardSDK, setSurfboardSDK] = useState<any>(null)
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [errorCallbackSet, setErrorCallbackSet] = useState(false)
  const [paymentStatusCallbackSet, setPaymentStatusCallbackSet] =
    useState(false)
  const [customerInfoSet, setCustomerInfoSet] = useState(false)
  const [usedPaymentMethod, setUsedPaymentMethod] =
    useState<SurfboardPaymentMethod | null>(null)

  // Update callback setters to check if they've already been set
  const setErrorCallback = useCallback(
    (callback: (code: string, message: string) => void) => {
      if (!surfboardSDK || errorCallbackSet) return
      surfboardSDK.errorCallback(callback)
      setErrorCallbackSet(true)
    },
    [surfboardSDK, errorCallbackSet]
  )

  const setPaymentStatusCallback = useCallback(
    (callback: (data: SurfboardPaymentStatusCallbackData) => void) => {
      if (!surfboardSDK || paymentStatusCallbackSet) return
      surfboardSDK.paymentStatusCallback(callback)
      setPaymentStatusCallbackSet(true)
    },
    [surfboardSDK, paymentStatusCallbackSet]
  )

  // Add new function to handle customer information
  const addCustomerInformationOnce =
    useCallback<SurfboardAddCustomerInformation>(
      async (customerInfo) => {
        if (!surfboardSDK || !isReady || customerInfoSet) return false
        try {
          await surfboardSDK.order.addCustomerInformation(customerInfo)
          setCustomerInfoSet(true)
          addLog('Customer information added successfully', 'success')
          return true
        } catch (error) {
          addLog(`Failed to add customer information: ${error}`, 'error')
          return false
        }
      },
      [surfboardSDK, isReady, customerInfoSet]
    )

  // Handle script load event
  const handleScriptLoad = () => {
    setScriptLoaded(true)
    addLog('Surfboard SDK script loaded successfully', 'success')
  }

  // Handle script error
  const handleScriptError = () => {
    addLog('Failed to load Surfboard SDK script', 'error')
  }

  useEffect(() => {
    if (!scriptLoaded) return

    const initializeSurfboard = async () => {
      if (typeof window === 'undefined') return

      try {
        // Wait for SDK to load with retry mechanism
        let retryCount = 0
        while (!(window as any).SurfboardOnlineSDK) {
          if (retryCount > 10) {
            throw new Error(
              'SurfboardOnlineSDK not available after maximum retries'
            )
          }
          await new Promise((resolve) => setTimeout(resolve, 500))
          retryCount++
        }

        const SDK = (window as any).SurfboardOnlineSDK
        addLog('Starting Surfboard SDK initialization')

        // Initialize SDK
        const config = {
          publicKey,
          orderId,
          nonce,
        }
        addLog(`SDK configuration: ${JSON.stringify(config)}`)

        await SDK.initialiseOnlineSDK(config)
        addLog('SDK initialized successfully')

        if (options.locale) {
          SDK.changeLocale(options.locale)
          addLog(`Locale changed to: ${options.locale}`)
        }

        setSurfboardSDK(SDK)
        setIsReady(true)
        addLog('Surfboard instance stored in context', 'success')
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error'
        addLog(`Failed to initialize Surfboard SDK: ${errorMessage}`, 'error')
        console.error('Failed to initialize Surfboard SDK:', error)
        setIsReady(false)
      }
    }

    initializeSurfboard()
  }, [scriptLoaded, publicKey, orderId, nonce, options.locale])

  return (
    <>
      {/* Add CSP meta tag */}
      <meta
        httpEquiv="Content-Security-Policy"
        content="default-src * 'unsafe-inline' 'unsafe-eval'; script-src * 'unsafe-inline' 'unsafe-eval'; connect-src * 'unsafe-inline'; img-src * data: blob: 'unsafe-inline'; frame-src * threeds.beta.surfboard.se; style-src * 'unsafe-inline';"
      />

      {/* Load Surfboard SDK script */}
      <Script
        src="https://thorium.surfgw.com/sdk/online/v1/surfboard-online-sdk.js"
        onLoad={handleScriptLoad}
        onError={handleScriptError}
        strategy="lazyOnload"
      />

      <SurfboardContext.Provider
        value={{
          setErrorCallback,
          setPaymentStatusCallback,
          isReady,
          paymentMethods: surfboardSDK?.paymentMethods,
          order: surfboardSDK?.order,
          mount: surfboardSDK?.mount,
          merchant: surfboardSDK?.merchant,
          branding: surfboardSDK?.branding,
          store: surfboardSDK?.store,
          customer: surfboardSDK?.customer,
          addCustomerInformationOnce,
          supportedPaymentMethods:
            surfboardSDK?.paymentMethods?.supportedPaymentMethods || [],
          initiatePayments: (method: SurfboardPaymentMethod) => {
            setUsedPaymentMethod(method)
            return surfboardSDK?.order?.initiatePayments(method)
          },
          usedPaymentMethod,
        }}
      >
        {children}
      </SurfboardContext.Provider>
    </>
  )
}

// Add a custom hook for payment methods
export const useSurfboardPaymentMethods = () => {
  const { supportedPaymentMethods, isReady } = useSurfboard()
  const [supportedMethods, setSupportedMethods] = useState<string[]>([])

  useEffect(() => {
    if (!isReady) return
    setSupportedMethods(supportedPaymentMethods)
  }, [supportedPaymentMethods, isReady])

  return supportedMethods
}

// Update PaymentElement to handle QR codes for Swish
export const PaymentElement: React.FC<PaymentElementProps> = ({ type }) => {
  const { t } = useTranslation('common')
  const elementRef = React.useRef<HTMLDivElement>(null)
  const { mount, isReady, initiatePayments } = useInternalSurfboard()
  const [qrData, setQrData] = useState<string | null>(null)

  const handlePayWithSwish = () => {
    initiatePayments('SSWISH')
  }

  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    if (!elementRef.current || !mount || !isReady || isMounted) return

    const mountConfig: Record<string, string> = {}
    switch (type) {
      case 'CARD':
        mountConfig.mountCardWidget = elementRef.current.id
        break
      case 'APPLE_PAY':
        mountConfig.mountApplePayWidget = elementRef.current.id
        break
      case 'GOOGLE_PAY':
        mountConfig.mountGooglePayWidget = elementRef.current.id
        break
      case 'KLARNA':
        mountConfig.mountKlarnaWidget = elementRef.current.id
        break
    }

    mount(mountConfig)
    setIsMounted(true)

    // Handle Swish QR code for desktop
    if (
      type === 'NSWISH' &&
      !/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    ) {
      initiatePayments('NSWISH').then((response: PaymentAttempt) => {
        if (response.getSwishQRData) {
          setQrData(response.getSwishQRData)
        }
      })
    }
  }, [mount, type, isReady, isMounted, initiatePayments])

  if (type === 'NSWISH' && qrData) {
    return (
      <div className="mt-4">
        <h3 className="mb-4 text-lg font-medium">{t('scan-with-swish-app')}</h3>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={qrData} alt="Swish QR Code" className="mx-auto h-64 w-64" />
      </div>
    )
  }

  if (type === 'SSWISH') {
    return (
      <div className="mt-4">
        <button
          onClick={handlePayWithSwish}
          className="mt-2 flex w-full items-center justify-center space-x-2 rounded-lg border border-blue-200 bg-blue-50 p-4 transition-colors hover:bg-blue-100"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 420 566"
            xmlns="http://www.w3.org/2000/svg"
            className="mr-2"
          >
            <defs>
              <linearGradient
                x1="80.8150544%"
                y1="59.6365716%"
                x2="59.8678229%"
                y2="8.14952521%"
                id="linearGradient-1"
              >
                <stop stop-color="#EF2131" offset="0%" />
                <stop stop-color="#FECF2C" offset="100%" />
              </linearGradient>
              <linearGradient
                x1="58.8181173%"
                y1="0%"
                x2="9.24395007%"
                y2="94.0712606%"
                id="linearGradient-2"
              >
                <stop stop-color="#FBC52C" offset="0%" />
                <stop stop-color="#F87130" offset="26.3921199%" />
                <stop stop-color="#EF52E2" offset="56.079734%" />
                <stop stop-color="#661EEC" offset="100%" />
              </linearGradient>
              <linearGradient
                x1="20.5690473%"
                y1="41.0176198%"
                x2="40.5972643%"
                y2="91.2765373%"
                id="linearGradient-3"
              >
                <stop stop-color="#78F6D8" offset="0%" />
                <stop stop-color="#77D1F6" offset="26.5705044%" />
                <stop stop-color="#70A4F3" offset="55.447147%" />
                <stop stop-color="#661EEC" offset="100%" />
              </linearGradient>
              <linearGradient
                x1="42.4403836%"
                y1="100%"
                x2="91.6136906%"
                y2="6.27757791%"
                id="linearGradient-4"
              >
                <stop stop-color="#536EED" offset="0%" />
                <stop stop-color="#54C3EC" offset="24.6657524%" />
                <stop stop-color="#64D769" offset="56.4199509%" />
                <stop stop-color="#FECF2C" offset="100%" />
              </linearGradient>
            </defs>
            <g
              id="01-Primary-(Vertical)/Colour/Light-background/Swish_Logo_Primary_Light-BG"
              stroke="none"
              stroke-width="1"
              fill="none"
              fill-rule="evenodd"
            >
              <g
                id="Wordmark/Swish"
                transform="translate(54.000000, 470.000000)"
                fill="#4A4A49"
                fill-rule="nonzero"
              >
                <path
                  d="M327.407874,15.8757003 C327.407874,13.8495723 327.781624,11.9501057 328.529134,10.1772437 C329.276644,8.40438161 330.291107,6.85814918 331.572553,5.53849999 C332.854,4.21885079 334.355672,3.17914278 336.077615,2.41934476 C337.799559,1.65954674 339.648284,1.27965343 341.623847,1.27965343 C343.626107,1.27965343 345.501529,1.65954674 347.250169,2.41934476 C348.998809,3.17914278 350.520503,4.21885079 351.815298,5.53849999 C353.110093,6.85814918 354.13123,8.40438161 354.87874,10.1772437 C355.62625,11.9501057 356,13.8495723 356,15.8757003 C356,17.9018284 355.62625,19.8012949 354.87874,21.574157 C354.13123,23.347019 353.110093,24.8932515 351.815298,26.2129007 C350.520503,27.5325499 348.998809,28.5722579 347.250169,29.3320559 C345.501529,30.0918539 343.626107,30.4717472 341.623847,30.4717472 C339.648284,30.4717472 337.799559,30.0918539 336.077615,29.3320559 C334.355672,28.5722579 332.854,27.5325499 331.572553,26.2129007 C330.291107,24.8932515 329.276644,23.347019 328.529134,21.574157 C327.781624,19.8012949 327.407874,17.9018284 327.407874,15.8757003 Z M330.331159,15.8757003 C330.331159,17.5552539 330.618145,19.1148159 331.192126,20.5544332 C331.766107,21.9940433 332.560325,23.2470319 333.574803,24.3134151 C334.589281,25.3797983 335.783945,26.2195625 337.15883,26.8327328 C338.533715,27.4459031 340.022039,27.7524837 341.623847,27.7524837 C343.252352,27.7524837 344.760698,27.4459031 346.148931,26.8327328 C347.537165,26.2195625 348.745177,25.3797983 349.773003,24.3134151 C350.80083,23.2470319 351.608396,21.9940433 352.195726,20.5544332 C352.783055,19.1148159 353.076715,17.5552539 353.076715,15.8757003 C353.076715,14.1961468 352.783055,12.6365848 352.195726,11.1969675 C351.608396,9.75735018 350.80083,8.50436873 349.773003,7.43798555 C348.745177,6.37160236 347.537165,5.53183819 346.148931,4.91866786 C344.760698,4.30549753 343.252352,3.99891696 341.623847,3.99891696 C340.022039,3.99891696 338.533715,4.30549753 337.15883,4.91866786 C335.783945,5.53183819 334.589281,6.37160236 333.574803,7.43798555 C332.560325,8.50436873 331.766107,9.75735018 331.192126,11.1969675 C330.618145,12.6365848 330.331159,14.1961468 330.331159,15.8757003 Z M336.738358,9.5974007 C336.738358,8.71763457 337.178848,8.27775811 338.059843,8.27775811 L342.584927,8.27775811 C344.026554,8.27775811 345.174499,8.69097539 346.028796,9.51742236 C346.883094,10.3438693 347.310236,11.4635549 347.310236,12.8765126 C347.310236,13.4630234 347.223473,13.9828774 347.049944,14.4360902 C346.876415,14.8893031 346.656169,15.282526 346.389201,15.6157707 C346.122233,15.9490155 345.828573,16.2222721 345.508211,16.4355487 C345.18785,16.6488253 344.867493,16.7954508 344.547132,16.8754296 L344.547132,16.9554079 C344.573828,16.9820675 344.613873,17.0353858 344.667267,17.1153646 C344.72066,17.1686837 344.774053,17.2553261 344.827447,17.3752942 C344.88084,17.4952623 344.960929,17.635223 345.067717,17.7951805 L347.150056,21.8340866 C347.390327,22.2872995 347.44372,22.6538632 347.310236,22.9337888 C347.176752,23.2137144 346.883092,23.353675 346.429246,23.353675 L345.948706,23.353675 C345.201196,23.353675 344.680616,23.0337649 344.386952,22.393935 L342.064342,17.5152563 L339.581552,17.5152563 L339.581552,22.0740216 C339.581552,22.9271282 339.167758,23.353675 338.340157,23.353675 L337.979753,23.353675 C337.152152,23.353675 336.738358,22.9271282 336.738358,22.0740216 L336.738358,9.5974007 Z M341.984252,15.4358195 C342.758459,15.4358195 343.359128,15.2092164 343.786277,14.7560036 C344.213425,14.3027907 344.426997,13.6763 344.426997,12.8765126 C344.426997,12.1033848 344.213425,11.5035533 343.786277,11.077 C343.359128,10.6504467 342.771807,10.4371733 342.024297,10.4371733 L339.581552,10.4371733 L339.581552,15.4358195 L341.984252,15.4358195 Z M225.25602,24.9972299 C229.78313,24.9972299 233.622238,25.5734014 236.773461,26.7257618 C239.924684,27.8781221 242.387927,28.9418234 244.163264,29.9168975 C245.672301,30.7146854 246.559956,31.7783867 246.826257,33.1080332 C247.092557,34.4376798 246.870643,35.8559481 246.160509,37.3628809 L244.829012,39.7562327 C244.030111,41.3518085 243.009307,42.2825471 241.766571,42.5484765 C240.523836,42.8144058 239.059205,42.5928013 237.372634,41.8836565 C235.863598,41.1745117 234.043905,40.4432171 231.913501,39.6897507 C229.783096,38.9362843 227.297662,38.5595568 224.457123,38.5595568 C221.527817,38.5595568 219.264296,39.1357283 217.666493,40.2880886 C216.068689,41.440449 215.2698,43.0803218 215.2698,45.2077562 C215.2698,47.0692614 216.090881,48.5761715 217.733068,49.7285319 C219.375254,50.8808922 221.461244,51.9002726 223.991099,52.7867036 C226.520954,53.6731346 229.228302,54.6260337 232.113225,55.6454294 C234.998148,56.664825 237.705496,57.9722912 240.235351,59.567867 C242.765206,61.1634429 244.851196,63.2022037 246.493383,65.6842105 C248.135569,68.1662174 248.95665,71.3130003 248.95665,75.1246537 C248.95665,78.1385192 248.357483,80.9085746 247.159131,83.434903 C245.960778,85.9612315 244.229851,88.1551153 241.966296,90.0166205 C239.702741,91.8781257 236.973202,93.3407149 233.777595,94.4044321 C230.581989,95.4681494 227.031368,96 223.125627,96 C217.622082,96 212.984084,95.202224 209.211493,93.6066482 C205.438902,92.0110724 202.531831,90.5041622 200.490194,89.0858726 C198.981157,88.1994416 198.137885,87.0914194 197.960351,85.7617729 C197.782817,84.4321263 198.182262,83.013858 199.158697,81.5069252 L200.756493,79.1135734 C201.732928,77.6952838 202.820306,76.8975078 204.018658,76.7202216 C205.217011,76.5429354 206.65945,76.8975025 208.34602,77.7839335 C209.943824,78.6703645 212.007622,79.6675845 214.537477,80.7756233 C217.067332,81.883662 220.063168,82.4376731 223.525075,82.4376731 C226.454381,82.4376731 228.762285,81.8171807 230.448855,80.5761773 C232.135425,79.3351739 232.978697,77.6509802 232.978697,75.5235457 C232.978697,73.6620406 232.157616,72.1772909 230.51543,71.0692521 C228.873243,69.9612133 226.787253,68.9418329 224.257398,68.0110803 C221.727543,67.0803278 219.020195,66.0831078 216.135272,65.0193906 C213.25035,63.9556734 210.543001,62.6260468 208.013146,61.0304709 C205.483291,59.4348951 203.397301,57.3961343 201.755115,54.9141274 C200.112928,52.4321206 199.291847,49.2410167 199.291847,45.3407202 C199.291847,42.0609254 199.97978,39.1357469 201.355666,36.565097 C202.731552,33.994447 204.595628,31.8670444 206.947949,30.1828255 C209.300271,28.4986065 212.052002,27.2133008 215.203225,26.3268698 C218.354448,25.4404388 221.705346,24.9972299 225.25602,24.9972299 Z M27.336639,24.9972299 C31.8637482,24.9972299 35.7028568,25.5734014 38.8540799,26.7257618 C42.005303,27.8781221 44.4685461,28.9418234 46.2438831,29.9168975 C47.7529195,30.7146854 48.6405747,31.7783867 48.9068752,33.1080332 C49.1731758,34.4376798 48.951262,35.8559481 48.2411272,37.3628809 L46.9096311,39.7562327 C46.1107295,41.3518085 45.089926,42.2825471 43.8471902,42.5484765 C42.6044543,42.8144058 41.1398233,42.5928013 39.4532532,41.8836565 C37.9442167,41.1745117 36.1245237,40.4432171 33.9941193,39.6897507 C31.8637149,38.9362843 29.3782805,38.5595568 26.5377413,38.5595568 C23.6084354,38.5595568 21.3449147,39.1357283 19.7471114,40.2880886 C18.1493082,41.440449 17.3504185,43.0803218 17.3504185,45.2077562 C17.3504185,47.0692614 18.1714995,48.5761715 19.8136862,49.7285319 C21.4558729,50.8808922 23.5418626,51.9002726 26.0717177,52.7867036 C28.6015729,53.6731346 31.3089212,54.6260337 34.1938437,55.6454294 C37.0787663,56.664825 39.7861145,57.9722912 42.3159697,59.567867 C44.8458249,61.1634429 46.9318145,63.2022037 48.5740012,65.6842105 C50.2161879,68.1662174 51.0372689,71.3130003 51.0372689,75.1246537 C51.0372689,78.1385192 50.4381017,80.9085746 49.2397492,83.434903 C48.0413968,85.9612315 46.3104692,88.1551153 44.0469146,90.0166205 C41.78336,91.8781257 39.0538203,93.3407149 35.8582138,94.4044321 C32.6626073,95.4681494 29.1119866,96 25.2062453,96 C19.7027007,96 15.0647025,95.202224 11.2921114,93.6066482 C7.51952039,92.0110724 4.61244972,90.5041622 2.57081222,89.0858726 C1.0617758,88.1994416 0.218503392,87.0914194 0.0409696963,85.7617729 C-0.136564,84.4321263 0.262880825,83.013858 1.23931615,81.5069252 L2.83711143,79.1135734 C3.81354676,77.6952838 4.90092433,76.8975078 6.09927678,76.7202216 C7.29762923,76.5429354 8.74006888,76.8975025 10.426639,77.7839335 C12.0244423,78.6703645 14.0882405,79.6675845 16.6180957,80.7756233 C19.1479508,81.883662 22.143787,82.4376731 25.6056941,82.4376731 C28.5350001,82.4376731 30.8429035,81.8171807 32.5294736,80.5761773 C34.2160437,79.3351739 35.0593162,77.6509802 35.0593162,75.5235457 C35.0593162,73.6620406 34.2382351,72.1772909 32.5960484,71.0692521 C30.9538617,69.9612133 28.8678721,68.9418329 26.3380169,68.0110803 C23.8081618,67.0803278 21.1008135,66.0831078 18.215891,65.0193906 C15.3309684,63.9556734 12.6236201,62.6260468 10.093765,61.0304709 C7.5639098,59.4348951 5.47792016,57.3961343 3.83573348,54.9141274 C2.19354679,52.4321206 1.37246576,49.2410167 1.37246576,45.3407202 C1.37246576,42.0609254 2.06039851,39.1357469 3.43628466,36.565097 C4.8121708,33.994447 6.67624665,31.8670444 9.02856812,30.1828255 C11.3808896,28.4986065 14.1326206,27.2133008 17.2838437,26.3268698 C20.4350668,25.4404388 23.7859651,24.9972299 27.336639,24.9972299 Z M270.12043,0 C273.848637,0 275.712713,1.86147723 275.712713,5.58448753 L275.712713,32.9750693 C275.712713,33.8615003 275.690522,34.6371158 275.646138,35.3019391 C275.601755,35.9667623 275.535181,36.5650943 275.446414,37.0969529 C275.357647,37.7174546 275.313264,38.2493053 275.313264,38.6925208 L275.579564,38.6925208 C276.378465,37.0969449 277.510226,35.4792326 278.974879,33.8393352 C280.439532,32.1994378 282.170459,30.7146881 284.167713,29.3850416 C286.164967,28.055395 288.428488,26.9916937 290.958343,26.1939058 C293.488198,25.3961179 296.217738,24.9972299 299.147044,24.9972299 C306.692226,24.9972299 312.528559,27.0359907 316.656217,31.1135734 C320.783876,35.1911561 322.847674,41.7506473 322.847674,50.7922438 L322.847674,88.8199446 C322.847674,92.5429549 320.983598,94.4044321 317.25539,94.4044321 L311.529957,94.4044321 C307.80175,94.4044321 305.937674,92.5429549 305.937674,88.8199446 L305.937674,54.2493075 C305.937674,50.0830817 305.22755,46.7368547 303.80728,44.2105263 C302.387011,41.6841979 299.546514,40.4210526 295.285705,40.4210526 C292.356399,40.4210526 289.671242,40.9972242 287.230154,42.1495845 C284.789066,43.3019448 282.725268,44.8753363 281.038697,46.8698061 C279.352127,48.8642759 278.042836,51.2354433 277.110784,53.9833795 C276.178732,56.7313157 275.712713,59.7008151 275.712713,62.8919668 L275.712713,88.8199446 C275.712713,92.5429549 273.848637,94.4044321 270.12043,94.4044321 L264.394997,94.4044321 C260.666789,94.4044321 258.802713,92.5429549 258.802713,88.8199446 L258.802713,54.2493075 C258.802713,1.86147723 260.666789,0 264.394997,0 L270.12043,0 Z M181.654029,26.5927978 C185.29347,26.5927978 187.113163,28.454275 187.113163,32.1772853 L187.113163,88.8199446 C187.113163,92.5429549 185.29347,94.4044321 181.654029,94.4044321 L175.795447,94.4044321 C172.156006,94.4044321 170.336313,92.5429549 170.336313,88.8199446 L170.336313,32.1772853 C170.336313,28.454275 172.156006,26.5927978 175.795447,26.5927978 L181.654029,26.5927978 Z M68.6780901,26.5927978 C72.0512303,26.5927978 74.0484544,28.2326706 74.6698223,31.5124654 L85.5880901,71.1357341 C85.7656238,72.1108082 85.9209634,72.9972259 86.0541137,73.7950139 C86.187264,74.5927978 86.3426036,75.3462568 86.5201373,76.0554017 C86.697671,76.8531896 86.8308193,77.6066447 86.9195861,78.3157895 L87.1858853,78.3157895 C87.2746522,77.6066447 87.4078005,76.8531896 87.5853342,76.0554017 C87.7628678,75.3462568 87.9182075,74.5927978 88.0513578,73.7950139 C88.184508,72.9972259 88.3842305,72.1108082 88.650531,71.1357341 L100.101397,31.5124654 C100.722765,28.3213137 102.764372,26.7257618 106.226279,26.7257618 L111.285964,26.7257618 C114.570337,26.7257618 116.611944,28.3213137 117.410846,31.5124654 L128.728562,71.1357341 C128.994863,72.1108082 129.194585,72.9972259 129.327736,73.7950139 C129.460886,74.5927978 129.616226,75.3462568 129.793759,76.0554017 C129.971293,76.8531896 130.104441,77.6066447 130.193208,78.3157895 L130.459507,78.3157895 C130.548274,77.6066447 130.681423,76.8531896 130.858956,76.0554017 C131.03649,75.3462568 131.19183,74.5927978 131.32498,73.7950139 C131.45813,72.9972259 131.657853,72.1108082 131.924153,71.1357341 L142.709271,31.5124654 C143.508173,28.2326706 145.54978,26.5927978 148.834153,26.5927978 L154.959035,26.5927978 C157.000672,26.5927978 158.420921,27.1689693 159.219822,28.3213296 C160.018724,29.47369 160.151872,30.9806001 159.619271,32.8421053 L142.176673,89.7506925 C141.289004,92.8532011 139.203015,94.4044321 135.918641,94.4044321 L126.997618,94.4044321 C123.624477,94.4044321 121.538488,92.8088802 120.739586,89.6177285 L110.487066,55.7119114 C110.220766,54.8254803 109.998852,53.9390626 109.821318,53.0526316 C109.643785,52.1662006 109.466254,51.3684246 109.28872,50.6592798 C109.111186,49.8614919 108.978038,49.1080368 108.889271,48.398892 L108.622972,48.398892 C108.445438,49.1080368 108.267907,49.8614919 108.090374,50.6592798 C107.91284,51.3684246 107.735309,52.1662006 107.557775,53.0526316 C107.380241,53.9390626 107.158328,54.8254803 106.892027,55.7119114 L96.6395074,89.6177285 C95.8406057,92.8088802 93.7989989,94.4044321 90.5146255,94.4044321 L81.3273027,94.4044321 C78.1316961,94.4044321 76.0900892,92.8532011 75.2024208,89.7506925 L57.6266727,32.8421053 C57.0940716,30.9806001 57.2494113,29.47369 58.0926964,28.3213296 C58.9359814,27.1689693 60.3340383,26.5927978 62.286909,26.5927978 L68.6780901,26.5927978 Z"
                  id="swish®"
                />
              </g>
              <g id="Symbol/Colour">
                <g
                  id="Right-Swirl"
                  transform="translate(119.332186, 49.114212)"
                >
                  <path
                    d="M0,350.04439 C84.2884683,390.356195 188.31202,370.40599 251.156312,295.526341 C325.643824,206.7744 314.074361,74.4625171 225.315044,-5.68434189e-14 L166.309346,70.306361 C235.651961,128.480254 244.690771,231.848605 186.49762,301.186302 C140.096429,356.473463 63.6990439,374.933385 0,350.04439"
                    id="Base-Shape"
                    fill="url(#linearGradient-1)"
                  />
                  <path
                    d="M1.13686838e-13,350.04439 C84.2884683,390.356195 188.31202,370.40599 251.156312,295.526341 C258.852761,286.356 265.627668,276.719766 271.496605,266.731141 C281.420898,205.05998 259.596059,139.875161 208.256898,96.8056976 C195.213132,85.8627512 181.092937,77.0468488 166.318361,70.3145561 C235.65278,128.489678 244.687902,231.851883 186.49762,301.186302 C140.096429,356.473463 63.6990439,374.933385 1.13686838e-13,350.04439"
                    id="Light-Cast"
                    fill="url(#linearGradient-2)"
                  />
                </g>
                <g id="Left-Swirl">
                  <path
                    d="M300.254951,20.4289088 C215.966893,-19.8828961 111.943342,0.0668990353 49.0990489,74.9465478 C-25.3884633,163.698899 -13.8189999,296.010782 74.9399074,370.473299 L133.946015,300.166938 C64.6029903,241.993045 55.5645903,138.624694 113.757742,69.2865868 C160.158932,13.9994259 236.556317,-4.46049609 300.254951,20.4289088"
                    id="Base-shape"
                    fill="url(#linearGradient-3)"
                  />
                  <path
                    d="M300.254951,20.4289088 C215.966893,-19.8828961 111.943342,0.0668990353 49.0990489,74.9465478 C41.4021903,84.117299 34.627283,93.7535332 28.7587562,103.742158 C18.8340538,165.413319 40.6593025,230.597728 91.9984635,273.667601 C105.042229,284.610138 119.162424,293.42645 133.93659,300.158333 C64.6025806,241.983621 55.5670489,138.621416 113.757742,69.2865868 C160.158932,13.9994259 236.556317,-4.46049609 300.254951,20.4289088"
                    id="Shadow"
                    fill="url(#linearGradient-4)"
                  />
                </g>
              </g>
            </g>
          </svg>
          <span className="font-medium text-gray-800">
            {t('pay-with-swish')}
          </span>
        </button>
      </div>
    )
  }

  if (type === 'KLARNA') {
    return (
      <div className="mt-4">
        <div
          ref={elementRef}
          id={`surfboard-${type.toLowerCase()}`}
          className="mb-3"
        ></div>
        <button
          className="mt-2 flex w-full items-center justify-center space-x-2 rounded-lg border border-pink-200 bg-pink-50 p-4 transition-colors hover:bg-pink-100"
          onClick={() => initiatePayments('KLARNA')}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 -9 58 58"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mr-2"
          >
            <rect
              x="0.5"
              y="0.5"
              width="57"
              height="39"
              rx="3.5"
              fill="#F4B6C7"
              stroke="#F3F3F3"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M7.41299 25.7963H5V15H7.41299V25.7963ZM13.4398 15H11.0784C11.0784 16.9868 10.1885 18.8104 8.63709 20.0034L7.70155 20.7226L11.3264 25.7967H14.3068L10.9714 21.1277C12.5525 19.5116 13.4398 17.3373 13.4398 15ZM17.289 25.7933H15.0102V15.0021H17.289V25.7933ZM24.1766 18.3286V18.8061C23.5616 18.3754 22.8192 18.1223 22.0185 18.1223C19.8993 18.1223 18.1815 19.8857 18.1815 22.0611C18.1815 24.2365 19.8993 26 22.0185 26C22.8192 26 23.5616 25.7469 24.1766 25.3163V25.7933H26.3539V18.3286H24.1766ZM24.1694 22.0611C24.1694 23.1218 23.2861 23.9818 22.1966 23.9818C21.1071 23.9818 20.2238 23.1218 20.2238 22.0611C20.2238 21.0004 21.1071 20.1407 22.1966 20.1407C23.2861 20.1407 24.1694 21.0004 24.1694 22.0611ZM47.1454 18.8061V18.3286H49.3226V25.7933H47.1454V25.3163C46.5304 25.7469 45.788 26 44.9872 26C42.868 26 41.1502 24.2365 41.1502 22.0611C41.1502 19.8857 42.868 18.1223 44.9872 18.1223C45.788 18.1223 46.5304 18.3754 47.1454 18.8061ZM45.1654 23.9818C46.255 23.9818 47.1381 23.1218 47.1381 22.0611C47.1381 21.0004 46.255 20.1407 45.1654 20.1407C44.0758 20.1407 43.1926 21.0004 43.1926 22.0611C43.1926 23.1218 44.0758 23.9818 45.1654 23.9818ZM50.2675 24.5482C50.2675 23.7736 50.8792 23.1457 51.6337 23.1457C52.3882 23.1457 53 23.7736 53 24.5482C53 25.3227 52.3882 25.9507 51.6337 25.9507C50.8792 25.9507 50.2675 25.3227 50.2675 24.5482ZM37.2814 18.1278C36.4117 18.1278 35.5887 18.405 35.0384 19.1697V18.329H32.8706V25.7933H35.065V21.8706C35.065 20.7354 35.8065 20.1796 36.6993 20.1796C37.6562 20.1796 38.2063 20.7663 38.2063 21.8551V25.7933H40.3809V21.0463C40.3809 19.3092 39.0354 18.1278 37.2814 18.1278ZM29.7219 18.3287V19.3009C30.1583 18.7177 30.9715 18.3291 31.8557 18.3291V20.5013L31.8487 20.501L31.8435 20.5008L31.8379 20.5005L31.8298 20.5003C30.9684 20.5003 29.7269 21.1323 29.7269 22.3082V25.7933H27.4928V18.3287H29.7219Z"
              fill="#17120F"
            />
          </svg>
          <span className="font-medium text-gray-800">
            {t('pay-with-klarna')}
          </span>
        </button>
      </div>
    )
  }

  return <div ref={elementRef} id={`surfboard-${type.toLowerCase()}`} />
}
